import { useEffect } from 'react';

import { BackrsForm, InfoModal, BackrsFormValues, PriorityPlaylist } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { playlistDialogFormConfig } from '@backrs_ui/grow-ui/src/pages/Priorities/configs/playlistDialogForm.config';
import { PriorityPlaylistPost } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Theme, SxProps, Typography, Button } from '@mui/material';
import { useForm } from 'react-hook-form';

const PLAYLIST_DIALOG_STRINGS = appStrings.modules.priorities.playlistDialog;

type PlaylistDialogProps = Readonly<{
  isPendingAddNewNugget: boolean;
  preLoadedTitle?: string;
  playlistToEdit?: PriorityPlaylist | null;
  handleClose: (shouldClosePlaylistDrawer: boolean) => void;
  handlePlaylistCreate: (playlist: PriorityPlaylistPost) => Promise<void>;
  handlePlaylistUpdate?: (playlist: PriorityPlaylistPost) => Promise<void>;
}>;

export const PlaylistDialog = ({
  isPendingAddNewNugget,
  preLoadedTitle = '',
  playlistToEdit,
  handleClose,
  handlePlaylistCreate,
  handlePlaylistUpdate,
}: PlaylistDialogProps) => {
  const fields = playlistDialogFormConfig({
    playlistToEdit,
  });

  const methods = useForm<BackrsFormValues>({
    defaultValues: {
      title: playlistToEdit?.title || preLoadedTitle,
      description: playlistToEdit?.description || '',
      tags: '',
    },
  });

  // Handle tags transformation for editing
  useEffect(() => {
    if (playlistToEdit?.tags && playlistToEdit.tags.length > 0) {
      const tagTitles = playlistToEdit.tags.map((tag) => tag.title);

      (methods.setValue as (name: string, value: unknown) => void)('tags', tagTitles);
    }
  }, [playlistToEdit, methods]);

  const isFormStateValid = methods.formState.isValid;
  const isEditing = !!playlistToEdit;

  const handleSubmit = async (data: BackrsFormValues) => {
    try {
      const tags = Array.isArray(data.tags) ? data.tags : [];
      const uniqueTags = tags.filter((tag, index, self) => self.indexOf(tag) === index);

      const constructedPlaylist: PriorityPlaylistPost = {
        title: data.title as string,
        description: data.description as string,
        tags: uniqueTags,
      };

      if (isEditing && handlePlaylistUpdate) {
        await handlePlaylistUpdate(constructedPlaylist);
        handleClose(false);

        return;
      }

      if (isPendingAddNewNugget) {
        await handlePlaylistCreate(constructedPlaylist);

        return;
      }

      await handlePlaylistCreate(constructedPlaylist);
      handleClose(false);
    } catch (e) {
      console.log('Error saving playlist', e);
    }
  };

  return (
    <InfoModal
      open={true}
      width="sm"
      onClose={() => handleClose(false)}
      hideCircleImage
      featuredIcon={{
        iconName: 'Flag05',
        size: 'lg',
        color: 'Colors/Foreground/fg-secondary',
      }}
      headerInLineWithIcon
      showBottomBorder
      dialogSx={dialogSx}
      headerContent={
        <Box>
          <Typography variant="text-lg-semibold">
            {isEditing ? PLAYLIST_DIALOG_STRINGS.editTitle : PLAYLIST_DIALOG_STRINGS.addPlaylist}
          </Typography>
          <Typography variant="text-sm-regular">
            {isEditing
              ? PLAYLIST_DIALOG_STRINGS.editDescription
              : PLAYLIST_DIALOG_STRINGS.description}
          </Typography>
        </Box>
      }
      bodyContainerSx={bodyContainerSx}
      bodyContent={
        <Box>
          <Box sx={formContainerSx}>
            <BackrsForm fields={fields} onSubmit={handleSubmit} methods={methods} />
          </Box>

          <Box sx={buttonContainerSx}>
            <Button
              id="playlist-dialog-cancel-button"
              variant="contained"
              hierarchy="Secondary gray"
              fullWidth
              size="lg"
              onClick={() => handleClose(false)}
            >
              {PLAYLIST_DIALOG_STRINGS.cancel}
            </Button>
            <Button
              id="playlist-dialog-submit-button"
              variant="contained"
              hierarchy="Primary"
              fullWidth
              size="lg"
              onClick={() => handleSubmit(methods.getValues())}
              disabled={!isFormStateValid}
            >
              {isEditing
                ? PLAYLIST_DIALOG_STRINGS.updatePlaylist
                : PLAYLIST_DIALOG_STRINGS.addPlaylist}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-none'],
  marginX: -backrs.spacing['spacing-3xl'],
  width: '100%',
});

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-2xl'],
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  p: backrs.spacing['spacing-3xl'],
});

const formContainerSx: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-3xl'],
});

const dialogSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '640px', // specific pixel width to match Figma
    width: '640px',
    margin: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-4xl'] },
  },
});
